import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Code, Rocket, Heart, Target, Award, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Page Hero with Personal Story
const PageHero = () => {
  return (
    <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#030303] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#D4AF37] rounded-full filter blur-[100px] sm:blur-[150px]" />
      </div>
      
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left - Text */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="order-2 lg:order-1"
          >
            <motion.p variants={fadeInUp} className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">
              A történetem
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6 leading-tight">
              Nem egy ügynökség.{" "}
              <span className="italic-accent text-gold-gradient">Egy ember</span>,{" "}
              aki érti a dolgát.
            </motion.h1>
            <motion.div variants={fadeInUp} className="gold-line mb-6 sm:mb-8" />
          </motion.div>

          {/* Right - Logo Emblem */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="order-1 lg:order-2"
          >
            <div className="aspect-square sm:aspect-[4/5] relative flex items-center justify-center bg-[#050505] border border-white/[0.04] overflow-hidden">
              {/* Atmospheric glow */}
              <div className="absolute inset-0">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-[#D4AF37]/[0.04] rounded-full blur-[80px]" />
              </div>
              {/* Subtle corner accents */}
              <div className="absolute top-0 left-0 w-12 h-px bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
              <div className="absolute top-0 left-0 w-px h-12 bg-gradient-to-b from-[#D4AF37]/40 to-transparent" />
              <div className="absolute bottom-0 right-0 w-12 h-px bg-gradient-to-l from-[#D4AF37]/40 to-transparent" />
              <div className="absolute bottom-0 right-0 w-px h-12 bg-gradient-to-t from-[#D4AF37]/40 to-transparent" />
              {/* Logo */}
              <motion.img
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
                src={`${process.env.REACT_APP_BACKEND_URL}/api/images/stryx-portfolio/references/stryx-logo.png`}
                alt="Stryx"
                className="w-[55%] sm:w-[50%] h-auto relative z-10 drop-shadow-[0_0_40px_rgba(212,175,55,0.08)]"
              />
              {/* Bottom gradient fade */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#050505] to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Story Section
const StorySection = () => {
  return (
    <section className="py-16 sm:py-24 bg-[#050505]" data-testid="story-section">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-4xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {/* Quote */}
          <motion.div variants={fadeInUp} className="relative mb-10 sm:mb-16">
            <span className="quote-mark top-0 left-0">"</span>
            <blockquote className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white italic leading-relaxed pl-8 sm:pl-12">
              20 évesen eldöntöttem, hogy a webfejlesztés lesz az életem. 
              Nem tudtam, hogy ez az út hova vezet – csak azt, hogy meg akarom csinálni.
            </blockquote>
          </motion.div>

          {/* Story Content */}
          <motion.div variants={fadeInUp} className="space-y-4 sm:space-y-6 text-zinc-400 leading-relaxed text-sm sm:text-base md:text-lg">
            <p>
              <span className="text-white font-medium">Éveket töltöttem a tanulással.</span> Nem egyetemen, 
              nem tanfolyamon – hanem éjszakáról éjszakára, projekt után projektet építve. 
              Hibákból tanultam, kudarcokból nőttem. Minden egyes sor kód, minden egyes design 
              egy lépés volt afelé, hogy jobbá váljak.
            </p>
            <p>
              Ma már <span className="text-[#D4AF37]">hatalmas projektekben veszek részt</span>. 
              Dolgozom startupokkal, akik a következő nagy dobásra készülnek. Építek webshopokat 
              vállalkozóknak, akik az egész életüket belerakták az álmaikba. Készítek weboldalakat 
              cégeknek, akik végre komolyan akarják venni az online jelenlétet.
            </p>
            <p>
              De ami a legfontosabb: <span className="text-white">megértem, mi kell ahhoz, hogy egy weboldal 
              ne csak szép legyen, hanem működjön is</span>. Hogy hozzon ügyfeleket, generáljon bevételt, 
              építsen márkát.
            </p>
            <p>
              Nem sablonokat árulok. Nem gyártósoros megoldásokat. Minden projektem egyedi, 
              mert minden ügyfelem egyedi. És ez nem marketing szöveg – ez a munkám lényege.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Values Section
const ValuesSection = () => {
  const values = [
    {
      icon: Target,
      title: "Eredményorientált",
      desc: "Nem az a célom, hogy szép oldalakat csináljak. Az a célom, hogy eredményes oldalakat csináljak."
    },
    {
      icon: Heart,
      title: "Szenvedély",
      desc: "Amit csinálok, azt szeretem. És ez meglátszik minden egyes projekten."
    },
    {
      icon: Code,
      title: "Minőség",
      desc: "Nem engedek kompromisszumot a minőségben. Soha."
    },
    {
      icon: Users,
      title: "Partnerség",
      desc: "Nem ügyfél vagy – partner. A projekted sikere az én sikerem is."
    }
  ];

  return (
    <section className="py-16 sm:py-24 bg-[#030303]" data-testid="values-section">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-10 sm:mb-16">
            <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">Értékek</p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl text-white mb-3 sm:mb-4">
              Amiben hiszek
            </h2>
            <div className="gold-line-center mt-4 sm:mt-6" />
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="premium-card p-4 sm:p-6 md:p-8 text-center"
              >
                <value.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#D4AF37] mx-auto mb-4 sm:mb-6" strokeWidth={1.5} />
                <h3 className="font-['Cormorant_Garamond'] text-base sm:text-lg md:text-xl text-white mb-2 sm:mb-3">
                  {value.title}
                </h3>
                <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: "50+", label: "Elkészült projekt" },
    { number: "5+", label: "Év tapasztalat" },
    { number: "100%", label: "Elégedett ügyfél" },
    { number: "24h", label: "Válaszidő" },
  ];

  return (
    <section className="py-12 sm:py-20 bg-[#050505]">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {stats.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="text-center"
            >
              <div className="stat-number text-2xl sm:text-4xl">{stat.number}</div>
              <p className="text-zinc-500 text-xs sm:text-sm mt-1 sm:mt-2">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-16 sm:py-24 bg-[#030303]">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-3xl text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">
            Dolgozzunk{" "}
            <span className="italic-accent text-gold-gradient">együtt</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-zinc-400 text-sm sm:text-base md:text-lg mb-8 sm:mb-10">
            Ha tetszik, amit látsz, és úgy érzed, jó csapat lehetnénk – 
            beszéljünk. Egy konzultáció nem kötelez semmire.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/kapcsolat">
              <Button className="btn-gold w-full sm:w-auto">
                Kapcsolatfelvétel
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Main About Page
export default function AboutPage() {
  return (
    <div>
      <PageHero />
      <StorySection />
      <StatsSection />
      <ValuesSection />
      <CTASection />
    </div>
  );
}
